import { useCallback, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	ITEM_FIELDS as CARD_FIELDS,
	checkItems,
	CornerStyle,
	RenderVariant,
	SegmentationTargets,
	updateNodeParameters,
	type UseExtensionParametersAndActionsProps,
	useExtensionsActions,
} from '@atlassian/company-hub-common';

import {
	CARDS_EXTENSION_VERSION_2,
	CARDS_EXTENSION_VERSION_3,
	CARDS_EXTENSION_VERSION_4,
	CARDS_EXTENSION_VERSION_5,
	CARDS_IMAGES_V5,
} from '../../constants';
import {
	Alignment,
	type Card,
	type CardsExtensionParameters,
	CardsLayout,
	ImagePosition,
	Orientation,
} from '../../types';

import type { CardsActions, UpdateField } from './types';

export const cardNewParametersV4 = {
	backgroundColor: token('elevation.surface.overlay'),
	textColor: token('color.text'),
};


const getNewCardImage = (cardsLength: number) => {
	const imageIndex = cardsLength % CARDS_IMAGES_V5.length;
	const { src, alt } = CARDS_IMAGES_V5[imageIndex] || CARDS_IMAGES_V5[0];
	return { imageSrc: src, imageAltText: alt };
};

export const createNewCardWithDefaultParameters = (defaultField?: UpdateField<Card>): Card => {
	return {
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		cardId: uuid(),
		link: '',
		isLinkUserEdited: false,
		title: '',
		isTitleUserEdited: false,
		description: '',
		isDescriptionUserEdited: false,
		imagePosition: ImagePosition.MIDDLE,
		...defaultField,
	};
};

export const updateLinkAndConfluencePageId = (
	link: string,
	confluencePageId?: string,
	isLinkUserEdited?: boolean,
	linkResourceType?: string,
) => {
	return {
		[CARD_FIELDS.LINK]: link,
		[CARD_FIELDS.CONFLUENCE_PAGE_ID]: confluencePageId || '',
		[CARD_FIELDS.IS_LINK_USER_EDITED]: isLinkUserEdited || false,
		[CARD_FIELDS.LINK_RESOURCE_TYPE]: linkResourceType || '',
	};
};

const fetchLocalIdMacroParameters = (
	editorActions: EditorActions,
	localId: string,
	isCardsV2GateEnabled: boolean,
): CardsExtensionParameters => {
	const localIdNode = editorActions?.getNodeByLocalId?.(localId)?.toJSON();
	let params = localIdNode?.attrs?.parameters;

	if (params === undefined) {
		return undefined as any;
	}

	// Make a copy of the params object to avoid mutating the original object
	params = { ...params };

	// If the extension was created prior to removal of type field, remove it from object here
	// This update is/was not done in combination with a feature gate
	delete params['type'];

	params = updateParamsToVersion2(params);
	params = updateParamsToVersion3(params);
	params = updateParamsToVersion4(params);

	if (isCardsV2GateEnabled) {
		params = updateParamsToVersion5(params);
	}

	return params;
};

const updateParamsToVersion2 = (params: CardsExtensionParameters): CardsExtensionParameters => {
	params.version = CARDS_EXTENSION_VERSION_2;
	// Set render variant to manual only if it is not set
	params.renderVariant ??= RenderVariant.MANUAL;
	return params;
};

const updateParamsToVersion3 = (params: CardsExtensionParameters): CardsExtensionParameters => {
	// Only set for cards extension
	// Always set version to 3
	params.version = CARDS_EXTENSION_VERSION_3;

	params.isAutoscrollEnabled ??= false;

	// Set orientation to stack only if it is not set
	params.orientation ??= Orientation.STACKED;
	// Set alignment to center only if it is not set
	params.alignment ??= Alignment.CENTER;

	if (
		params.segmentationTarget === SegmentationTargets.ALL &&
		params.audienceSegmentationGroupIds === undefined
	) {
		params.audienceSegmentationGroupIds = params.cards[0]?.audienceSegmentationGroupIds;
	}
	return params;
};

const updateParamsToVersion4 = (params: CardsExtensionParameters): CardsExtensionParameters => {
	params.version = CARDS_EXTENSION_VERSION_4;
	params.backgroundColor ??= token('elevation.surface.overlay');
	params.textColor ??= token('color.text');
	params.cornerStyle ??= CornerStyle.ROUNDED;
	params.isSocialStatsShown ??= false;
	params.isIconShown ??= false;
	return params;
};

const updateParamsToVersion5 = (params: CardsExtensionParameters): CardsExtensionParameters => {
	params.version = CARDS_EXTENSION_VERSION_5;
	params.layout ??= CardsLayout.FLEXIBLE_IMAGE_HEIGHT;
	params.isPaddingEnabled ??= true;
	params.isLabelShown ??= true;
	params.borderColor ??= DEFAULT_GRAY_COLOR;
	return params;
};

const removeProperties = (
	extensionsPropertiesToRemove: string[],
	extensionParameters: CardsExtensionParameters,
): CardsExtensionParameters => {
	const mutatedParameters = { ...extensionParameters };

	extensionsPropertiesToRemove.forEach((field) => {
		delete mutatedParameters[field as keyof CardsExtensionParameters];
	});

	return mutatedParameters;
};

const updateCardInList = (cards: Card[], cardId: string, updatedField: UpdateField<Card>): Card[] =>
	cards.map((card) => {
		if (card.cardId === cardId) {
			return { ...card, ...updatedField };
		}
		return card;
	});

export type UseCardsParametersAndActionsReturnType = {
	cardsActions: CardsActions;
	cardsParameters: CardsExtensionParameters;
};

export const useCardsExtensionParametersAndActions = ({
	localId,
	editorActions,
}: UseExtensionParametersAndActionsProps): UseCardsParametersAndActionsReturnType => {
	const [cardsParameters, setExtensionParameters] = useState<CardsExtensionParameters>(
		fetchLocalIdMacroParameters(editorActions, localId, fg('company_hub_cards_v2')),
	);
	const { setIsInAutoFocusMode, setSelectedToEditCardId } = useExtensionsActions();
	const { cards, segmentationTarget, isSegmentationShown } = cardsParameters ?? {};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const extensionNodeParams = fetchLocalIdMacroParameters(
			editorActions,
			localId,
			fg('company_hub_cards_v2'),
		);
		setExtensionParameters(extensionNodeParams);
	}, [editorActions, localId]);

	const updateExtensionParams = useCallback(
		(
			newExtensionParameters: UpdateField<CardsExtensionParameters>,
			options: { fieldsToRemove?: string[] } = {},
		) => {
			setExtensionParameters((prevParams) => {
				let mutatedFields = { ...prevParams };
				const fieldsToRemove = [...(options?.fieldsToRemove || [])];

				if (fieldsToRemove.length > 0) {
					mutatedFields = removeProperties(fieldsToRemove, prevParams);
				}

				const updatedParams = {
					...mutatedFields,
					...newExtensionParameters,
				} as CardsExtensionParameters;

				// Save params to ADF and state
				const success = updateNodeParameters(editorActions, updatedParams);

				if (!success) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'cardsExtension',
							action: 'error',
							actionSubject: 'cardsConfigPanel',
							attributes: {
								updatedFields: newExtensionParameters,
								version: updatedParams.version,
							},
						},
					}).fire();
				}

				return updatedParams;
			});
		},
		[editorActions, createAnalyticsEvent],
	);

	const addCard = useCallback(() => {
		const newCard = createNewCardWithDefaultParameters({
			[CARD_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]:
				isSegmentationShown && segmentationTarget === SegmentationTargets.ALL
					? cards[0]?.audienceSegmentationGroupIds
					: undefined,
			...getNewCardImage(cards.length),
		});

		updateExtensionParams({
			cards: [...(checkItems(cards) || []), newCard],
		});
		setIsInAutoFocusMode(true);
		setSelectedToEditCardId(newCard.cardId);
	}, [
		cards,
		segmentationTarget,
		isSegmentationShown,
		updateExtensionParams,
		setSelectedToEditCardId,
		setIsInAutoFocusMode,
	]);

	const removeCard = useCallback(
		(cardId: string) =>
			updateExtensionParams({
				cards: cards.filter((card) => card.cardId !== cardId),
			}),
		[cards, updateExtensionParams],
	);

	const updateMainFormField = useCallback(
		(updatedFields: UpdateField<CardsExtensionParameters>, options = {}) =>
			updateExtensionParams(updatedFields, options),
		[updateExtensionParams],
	);

	const updateCardFormField = useCallback(
		(cardId: string, updatedField: UpdateField<Card>) => {
			const updatedCards = updateCardInList(cards, cardId, updatedField);

			updateExtensionParams({ cards: updatedCards });
		},
		[cards, updateExtensionParams],
	);

	const updateCardFormFieldForAllCards = useCallback(
		(updatedField: UpdateField<Card>) => {
			const updatedCards = cards.map((card) => {
				return { ...card, ...updatedField };
			});

			updateExtensionParams({ cards: updatedCards });
		},
		[cards, updateExtensionParams],
	);

	const updateLayout = useCallback(
		(newLayout: CardsLayout) => {
			updateExtensionParams({ layout: newLayout });
		},
		[updateExtensionParams],
	);

	const updateBorderColor = useCallback(
		(newBorderColor: string) => {
			updateExtensionParams({ borderColor: newBorderColor });
		},
		[updateExtensionParams],
	);

	const updateCornerStyle = useCallback(
		(newCornerStyle: CornerStyle) => {
			updateExtensionParams({ cornerStyle: newCornerStyle });
		},
		[updateExtensionParams],
	);

	const cardsActions: CardsActions = useMemo(
		() => ({
			addCard,
			removeCard,
			updateMainFormField,
			updateCardFormField,
			updateCardFormFieldForAllCards,
			updateLayout,
			updateBorderColor,
			updateCornerStyle,
		}),
		[
			addCard,
			removeCard,
			updateMainFormField,
			updateCardFormField,
			updateCardFormFieldForAllCards,
			updateLayout,
			updateBorderColor,
			updateCornerStyle,
		],
	);

	return { cardsParameters, cardsActions };
};
